import { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, Search, Users, Cake, CalendarDays } from "lucide-react";
import AdminLayout from "../../layouts/AdminLayout";
import { apiRequest } from "../../utils/apiRequest";
import AddStaffModal from "../../components/AddStaffModal";
import StaffCreatedModal from "../../components/StaffCreatedModal";
import FullPageSpinner from "../../components/FullPageSpinner";
import EditStaffModal from "../../components/EditStaffModal";
import DeactivateStaffModal from "../../components/DeactivateStaffModal";
import DeleteStaffModal from "../../components/DeleteStaffModal";

const initialForm = {
  first_name: "",
  last_name: "",
  staff_id: "",
  pin: "",
  status: "active",
  birthday: "",
  email: "",
  phone_number: "",
  house_address: "",
  document_type: "",
  document_expiry: "",
  document_file: null,
};

const initialEditForm = {
  id: "",
  staff_id: "",
  first_name: "",
  last_name: "",
  pin: "",
  status: "active",
  birthday: "",
  email: "",
  phone_number: "",
  house_address: "",
  document_type: "",
  document_expiry: "",
  document_file: null,
  current_file_name: "",
  current_file_path: "",
};

export default function StaffPage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCreatedModal, setShowCreatedModal] = useState(false);
  const [createdStaff, setCreatedStaff] = useState(null);

  const [staffList, setStaffList] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingStaffId, setLoadingStaffId] = useState(false);

  const [error, setError] = useState("");
  const [modalError, setModalError] = useState("");
  const [formData, setFormData] = useState(initialForm);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalError, setEditModalError] = useState("");
  const [editFormData, setEditFormData] = useState(initialEditForm);
  const [updatingStaff, setUpdatingStaff] = useState(false);

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [staffToDeactivate, setStaffToDeactivate] = useState(null);
  const [deactivateError, setDeactivateError] = useState("");
  const [deactivatingStaff, setDeactivatingStaff] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deletingStaff, setDeletingStaff] = useState(false);
  const [openingAddModal, setOpeningAddModal] = useState(false);
  const [openingEditModal, setOpeningEditModal] = useState(false);

  // Birthday summary modal
  const [birthdayModalType, setBirthdayModalType] = useState(null); // 'month' | 'upcoming'


  const token = localStorage.getItem("staff_portal_token");

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    setError("");

    const res = await apiRequest({
      script: "admin-get-staff",
      token,
      data: {},
    });

    if (!res?.success) {
      setError(res?.error || "Failed to fetch staff");
      setLoading(false);
      return;
    }

    setStaffList(res.staff || []);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const fetchNextStaffId = async () => {
    setLoadingStaffId(true);
    setModalError("");

    const res = await apiRequest({
      script: "admin-get-next-staff-id",
      token,
      data: {},
    });

    setLoadingStaffId(false);

    if (!res?.success) {
      setModalError(res?.error || "Failed to generate staff ID");
      return "";
    }

    return res.staff_id || "";
  };

  const filteredStaff = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return staffList;

    return staffList.filter((staff) => {
      const fullName = `${staff.first_name} ${staff.last_name}`.toLowerCase();
      const staffId = String(staff.staff_id || "").toLowerCase();
      const status = String(staff.status || "").toLowerCase();

      return (
        fullName.includes(query) ||
        staffId.includes(query) ||
        status.includes(query)
      );
    });
  }, [staffList, search]);

  // Birthday helpers
  const birthdayStats = useMemo(() => {
    const now = new Date();
    const thisMonth = now.getMonth(); // 0-indexed
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const in7Days = new Date(today);
    in7Days.setDate(today.getDate() + 7);

    const monthBirthdays = [];
    const upcomingBirthdays = [];

    staffList.forEach((staff) => {
      if (!staff.birthday) return;
      const [, month, day] = staff.birthday.split("-").map(Number);
      const birthdayThisYear = new Date(now.getFullYear(), month - 1, day);

      // This month
      if (month - 1 === thisMonth) {
        monthBirthdays.push({ ...staff, _birthdayDate: birthdayThisYear });
      }

      // Upcoming: birthday falls within today + 7 days (could be next year wrap)
      let upcoming = new Date(now.getFullYear(), month - 1, day);
      if (upcoming < today) {
        upcoming = new Date(now.getFullYear() + 1, month - 1, day);
      }
      if (upcoming >= today && upcoming <= in7Days) {
        upcomingBirthdays.push({ ...staff, _birthdayDate: upcoming });
      }
    });

    monthBirthdays.sort((a, b) => a._birthdayDate - b._birthdayDate);
    upcomingBirthdays.sort((a, b) => a._birthdayDate - b._birthdayDate);

    return { monthBirthdays, upcomingBirthdays };
  }, [staffList]);

  const formatBirthday = (dateStr) => {
    if (!dateStr) return "—";
    const [, month, day] = dateStr.split("-").map(Number);
    return new Date(2000, month - 1, day).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
    });
  };

  const generatePin = () => {
    const pin = String(Math.floor(1000 + Math.random() * 9000));
    setFormData((prev) => ({
      ...prev,
      pin,
    }));
  };

  const generateEditPin = () => {
    const pin = String(Math.floor(1000 + Math.random() * 9000));
    setEditFormData((prev) => ({
      ...prev,
      pin,
    }));
  };

  const handleOpenModal = async () => {
    setModalError("");
    setFormData(initialForm);

    setShowAddModal(true);
    setOpeningAddModal(true);

    const nextStaffId = await fetchNextStaffId();

    if (nextStaffId) {
      setFormData((prev) => ({
        ...prev,
        staff_id: nextStaffId,
      }));
    }

    setOpeningAddModal(false);
  };

  const handleCloseModal = () => {
    if (saving) return;
    setShowAddModal(false);
    setModalError("");
    setFormData(initialForm);
    setOpeningAddModal(false);
  };

  const handleCloseCreatedModal = () => {
    setShowCreatedModal(false);
    setCreatedStaff(null);
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setFormData((prev) => ({
        ...prev,
        [name]: files?.[0] || null,
      }));
      return;
    }

    if (name === "pin") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 4);
      setFormData((prev) => ({
        ...prev,
        [name]: digitsOnly,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    setModalError("");

    if (
      !formData.first_name.trim() ||
      !formData.last_name.trim() ||
      !formData.pin.trim()
    ) {
      setModalError("First name, last name and PIN are required.");
      return;
    }

    if (!formData.staff_id.trim()) {
      setModalError(
        "Unable to generate staff ID. Please close and reopen the form.",
      );
      return;
    }

    if (!/^\d{4}$/.test(formData.pin.trim())) {
      setModalError("PIN must be exactly 4 digits.");
      return;
    }

    if (formData.document_file && !formData.document_type) {
      setModalError("Please select a document type for the uploaded file.");
      return;
    }

    if (formData.document_file && !formData.document_expiry) {
      setModalError("Please select a document expiry date.");
      return;
    }

    if (
      !formData.document_file &&
      (formData.document_type || formData.document_expiry)
    ) {
      setModalError(
        "Please upload a document file or clear the document fields.",
      );
      return;
    }

    setSaving(true);

    const payload = new FormData();
    payload.append("first_name", formData.first_name.trim());
    payload.append("last_name", formData.last_name.trim());
    payload.append("pin", formData.pin.trim());
    payload.append("status", "active");
    payload.append("birthday", formData.birthday || "");
    payload.append("email", formData.email?.trim() || "");
    payload.append("phone_number", formData.phone_number?.trim() || "");
    payload.append("house_address", formData.house_address?.trim() || "");

    if (formData.document_type) {
      payload.append("document_type", formData.document_type);
    }

    if (formData.document_expiry) {
      payload.append("document_expiry", formData.document_expiry);
    }

    if (formData.document_file) {
      payload.append("document_file", formData.document_file);
    }

    const res = await apiRequest({
      script: "admin-create-staff",
      token,
      data: payload,
    });

    setSaving(false);

    if (!res?.success) {
      setModalError(res?.error || "Failed to create staff");
      return;
    }

    const createdPin = formData.pin.trim();

    setShowAddModal(false);
    setFormData(initialForm);

    setCreatedStaff({
      staff_id: res?.staff?.staff_id,
      first_name: res?.staff?.first_name || formData.first_name.trim(),
      last_name: res?.staff?.last_name || formData.last_name.trim(),
      pin: createdPin,
    });
    setShowCreatedModal(true);

    fetchStaff();
  };

  const handleOpenEditModal = async (staff) => {
    setEditModalError("");
    setOpeningEditModal(true);
    setShowEditModal(true);

    setEditFormData({
      id: staff.id,
      staff_id: staff.staff_id || "",
      first_name: staff.first_name || "",
      last_name: staff.last_name || "",
      pin: "",
      status: staff.status || "active",
      birthday: staff.birthday || "",
      email: staff.email || "",
      phone_number: staff.phone_number || "",
      house_address: staff.house_address || "",
      document_type: staff.document_type || "",
      document_expiry: staff.expiry_date || "",
      document_file: null,
      current_file_name: staff.file_name || "",
      current_file_path: staff.file_path || "",
    });
    setOpeningEditModal(false);
  };

  const handleCloseEditModal = () => {
    if (updatingStaff) return;
    setShowEditModal(false);
    setEditModalError("");
    setEditFormData(initialEditForm);
    setOpeningEditModal(false);
  };

  const handleEditChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setEditFormData((prev) => ({
        ...prev,
        [name]: files?.[0] || null,
      }));
      return;
    }

    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateStaff = async (e) => {
    e.preventDefault();
    setEditModalError("");

    if (!String(editFormData.id).trim()) {
      setEditModalError("Invalid staff record.");
      return;
    }

    if (!editFormData.first_name.trim() || !editFormData.last_name.trim()) {
      setEditModalError("First name and last name are required.");
      return;
    }

    if (!["active", "inactive"].includes(editFormData.status)) {
      setEditModalError("Invalid status.");
      return;
    }

    if (editFormData.document_file && !editFormData.document_type) {
      setEditModalError("Please select a document type for the uploaded file.");
      return;
    }

    if (editFormData.document_file && !editFormData.document_expiry) {
      setEditModalError("Please select a document expiry date.");
      return;
    }

    setUpdatingStaff(true);

    const payload = new FormData();
    payload.append("id", editFormData.id);
    payload.append("first_name", editFormData.first_name.trim());
    payload.append("last_name", editFormData.last_name.trim());
    payload.append("birthday", editFormData.birthday || "");
    payload.append("email", editFormData.email?.trim() || "");
    payload.append("phone_number", editFormData.phone_number?.trim() || "");
    payload.append("house_address", editFormData.house_address?.trim() || "");
    payload.append("document_type", editFormData.document_type || "");
    payload.append("document_expiry", editFormData.document_expiry || "");

    if (editFormData.pin) {
      payload.append("pin", editFormData.pin.trim());
    }

    if (editFormData.document_file) {
      payload.append("document_file", editFormData.document_file);
    }

    const res = await apiRequest({
      script: "admin-update-staff",
      token,
      data: payload,
    });

    setUpdatingStaff(false);

    if (!res?.success) {
      setEditModalError(res?.error || "Failed to update staff.");
      return;
    }

    setShowEditModal(false);
    setEditModalError("");
    setEditFormData(initialEditForm);
    fetchStaff();
  };

  const handleOpenDeactivateModal = (staff) => {
    setDeactivateError("");
    setStaffToDeactivate(staff);
    setShowDeactivateModal(true);
  };

  const handleCloseDeactivateModal = () => {
    if (deactivatingStaff) return;
    setShowDeactivateModal(false);
    setStaffToDeactivate(null);
    setDeactivateError("");
  };

  const handleDeactivateStaff = async () => {
    if (!staffToDeactivate?.id) {
      setDeactivateError("Invalid staff record.");
      return;
    }

    setDeactivateError("");
    setDeactivatingStaff(true);

    const res = await apiRequest({
      script: "admin-deactivate-staff",
      token,
      data: {
        id: staffToDeactivate.id,
      },
    });

    setDeactivatingStaff(false);

    if (!res?.success) {
      setDeactivateError(res?.error || "Failed to deactivate staff.");
      return;
    }

    setShowDeactivateModal(false);
    setStaffToDeactivate(null);
    setDeactivateError("");
    fetchStaff();
  };

  const handleOpenDeleteModal = (staff) => {
    setDeleteError("");
    setStaffToDelete(staff);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    if (deletingStaff) return;
    setShowDeleteModal(false);
    setStaffToDelete(null);
    setDeleteError("");
  };

  const handleDeleteStaff = async () => {
    if (!staffToDelete?.id) {
      setDeleteError("Invalid staff record.");
      return;
    }

    setDeleteError("");
    setDeletingStaff(true);

    const res = await apiRequest({
      script: "admin-delete-staff",
      token,
      data: {
        id: staffToDelete.id,
      },
    });

    setDeletingStaff(false);

    if (!res?.success) {
      setDeleteError(res?.error || "Failed to delete staff.");
      return;
    }

    setShowDeleteModal(false);
    setStaffToDelete(null);
    setDeleteError("");
    fetchStaff();
  };

  const currentMonthName = new Date().toLocaleDateString("en-GB", { month: "long" });

  return (
    <AdminLayout title="Staff">
      <div className="space-y-6">

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3">
          {/* Total Staff */}
          <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-100">
              <Users size={22} className="text-slate-600" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Staff</p>
              <p className="mt-0.5 text-2xl font-bold text-slate-900">{staffList.length}</p>
            </div>
          </div>

          {/* This Month Birthdays */}
          <button
            type="button"
            onClick={() => setBirthdayModalType("month")}
            className="flex items-center gap-4 rounded-2xl border border-pink-200 bg-pink-50 p-5 shadow-sm text-left transition hover:bg-pink-100"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-pink-100">
              <Cake size={22} className="text-pink-600" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-pink-600">{currentMonthName} Birthdays</p>
              <p className="mt-0.5 text-2xl font-bold text-slate-900">{birthdayStats.monthBirthdays.length}</p>
            </div>
          </button>

          {/* Upcoming Birthdays */}
          <button
            type="button"
            onClick={() => setBirthdayModalType("upcoming")}
            className="col-span-2 md:col-span-1 flex items-center gap-4 rounded-2xl border border-violet-200 bg-violet-50 p-5 shadow-sm text-left transition hover:bg-violet-100"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-violet-100">
              <CalendarDays size={22} className="text-violet-600" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-violet-600">Upcoming Birthdays</p>
              <p className="mt-0.5 text-2xl font-bold text-slate-900">{birthdayStats.upcomingBirthdays.length}</p>
              <p className="text-[11px] text-slate-400">Next 7 days</p>
            </div>
          </button>
        </div>

        {/* Birthday drill-down modal */}
        {birthdayModalType && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
            onClick={() => setBirthdayModalType(null)}
          >
            <div
              className="relative w-full max-w-lg rounded-3xl bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {birthdayModalType === "month"
                      ? `${currentMonthName} Birthdays`
                      : "Upcoming Birthdays"}
                  </h2>
                  <p className="mt-0.5 text-sm text-slate-500">
                    {birthdayModalType === "month"
                      ? `Staff with birthdays this month`
                      : "Staff with birthdays in the next 7 days"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setBirthdayModalType(null)}
                  className="rounded-full p-2 text-2xl leading-none text-slate-500 hover:bg-slate-100"
                >
                  ✕
                </button>
              </div>

              <div className="max-h-[60vh] overflow-y-auto p-6">
                {(birthdayModalType === "month"
                  ? birthdayStats.monthBirthdays
                  : birthdayStats.upcomingBirthdays
                ).length === 0 ? (
                  <p className="py-8 text-center text-sm text-slate-500">
                    No staff with birthdays{" "}
                    {birthdayModalType === "month" ? "this month" : "in the next 7 days"}.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {(birthdayModalType === "month"
                      ? birthdayStats.monthBirthdays
                      : birthdayStats.upcomingBirthdays
                    ).map((staff) => (
                      <li
                        key={staff.id}
                        className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-3"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-700">
                            {staff.first_name?.[0]}{staff.last_name?.[0]}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              {staff.first_name} {staff.last_name}
                            </p>
                            <p className="text-xs text-slate-500">{staff.staff_id}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-slate-900">
                            {formatBirthday(staff.birthday)}
                          </p>
                          <span
                            className={`text-[11px] font-medium ${
                              staff.status === "active" ? "text-emerald-600" : "text-slate-400"
                            }`}
                          >
                            {staff.status === "active" ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-sm">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by staff ID, name or status..."
                className="w-full rounded-xl border border-slate-300 py-3 pl-10 pr-4 text-base outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>

            <button
              onClick={handleOpenModal}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              <Plus size={18} />
              Add Staff
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr className="text-left">
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Staff ID
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    First Name
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Last Name
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Status
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-10 text-center text-sm text-slate-500"
                    >
                      Loading staff...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-10 text-center text-sm text-red-600"
                    >
                      {error}
                    </td>
                  </tr>
                ) : filteredStaff.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-10 text-center text-sm text-slate-500"
                    >
                      No staff found.
                    </td>
                  </tr>
                ) : (
                  filteredStaff.map((staff) => (
                    <tr
                      key={staff.id}
                      className={`border-b border-slate-100 last:border-b-0 ${
                        staff.status === "inactive"
                          ? "bg-slate-50 opacity-60"
                          : "bg-white"
                      }`}
                    >
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">
                        {staff.staff_id}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700">
                        {staff.first_name}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700">
                        {staff.last_name}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                            staff.status === "active"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-200 text-slate-700"
                          }`}
                        >
                          {staff.status === "active" ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(staff)}
                            className="text-sm font-medium text-slate-900 hover:underline"
                          >
                            Edit
                          </button>

                          {staff.status === "active" ? (
                            <button
                              type="button"
                              onClick={() => handleOpenDeactivateModal(staff)}
                              className="text-sm font-medium text-amber-600 hover:underline"
                            >
                              Deactivate
                            </button>
                          ) : (
                            <span className="text-sm text-slate-400">
                              Deactivated
                            </span>
                          )}

                          <button
                            type="button"
                            onClick={() => handleOpenDeleteModal(staff)}
                            className="text-sm font-medium text-red-600 hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <AddStaffModal
          isOpen={showAddModal}
          opening={openingAddModal}
          formData={formData}
          modalError={modalError}
          saving={saving}
          loadingStaffId={loadingStaffId}
          onClose={handleCloseModal}
          onChange={handleChange}
          onSubmit={handleAddStaff}
          onGeneratePin={generatePin}
        />

        <EditStaffModal
          isOpen={showEditModal}
          opening={openingEditModal}
          formData={editFormData}
          modalError={editModalError}
          saving={updatingStaff}
          onClose={handleCloseEditModal}
          onChange={handleEditChange}
          onSubmit={handleUpdateStaff}
          onGeneratePin={generateEditPin}
        />

        <StaffCreatedModal
          isOpen={showCreatedModal}
          createdStaff={createdStaff}
          onClose={handleCloseCreatedModal}
        />

        <DeactivateStaffModal
          isOpen={showDeactivateModal}
          staff={staffToDeactivate}
          loading={deactivatingStaff}
          error={deactivateError}
          onClose={handleCloseDeactivateModal}
          onConfirm={handleDeactivateStaff}
        />

        <DeleteStaffModal
          isOpen={showDeleteModal}
          staff={staffToDelete}
          loading={deletingStaff}
          error={deleteError}
          onClose={handleCloseDeleteModal}
          onConfirm={handleDeleteStaff}
        />

        <FullPageSpinner
          show={
            loading ||
            saving ||
            updatingStaff ||
            deactivatingStaff ||
            deletingStaff
          }
          text={loading ? "Loading staff..." : "Processing..."}
        />
      </div>
    </AdminLayout>
  );
}
